/**
 * Mine oppdateringer — searchable overview of the current user's StatusUpdates.
 * Route: /dashboard/status-updates (legacy /dashboard/oppdateringer redirects here).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { StatusUpdateFeed } from "../../../components/dashboard/statusUpdates/StatusUpdateFeed";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { updateUrlWithException } from "../../../components/Utils";
import {
  fetchStatusUpdates,
  isStatusUpdateModuleUnavailable,
} from "../../../api/statusUpdates";
import { StatusUpdateUnavailableNotice } from "../../../components/dashboard/statusUpdates/StatusUpdateUnavailableNotice";

const PER_PAGE = 25;

function resolveTotalCount(res, rowsLength) {
  const raw = res?.totalCount ?? res?.total_count;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  return rowsLength;
}

export function RedirectOppdateringerToStatusUpdates() {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return (
    <Navigate
      to={qs ? `/dashboard/status-updates?${qs}` : "/dashboard/status-updates"}
      replace
    />
  );
}

export function MyStatusUpdates() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [queryInput, setQueryInput] = useState(searchParams.get("q") || "");
  const [query, setQuery] = useState(searchParams.get("q") || "");

  const skipUrlSyncRef = useRef(true);
  const requestIdRef = useRef(0);

  const debouncedSetQuery = useMemo(
    () =>
      debounce((value) => {
        setQuery(value);
        setPage(1);
      }, 300),
    []
  );

  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    updateUrlWithException({ q: query || "" }, "filters");
  }, [query]);

  const loadPage = useCallback(
    async (pageNum, { append = false, q } = {}) => {
      const requestId = ++requestIdRef.current;
      if (append) setLoadingMore(true);
      else {
        setLoading(true);
        setLoadError(false);
      }
      try {
        const res = await fetchStatusUpdates({
          mine: true,
          q,
          page: pageNum,
          perPage: PER_PAGE,
        });
        if (requestId !== requestIdRef.current) return;

        if (isStatusUpdateModuleUnavailable(res)) {
          setUnavailable(true);
          setLoadError(false);
          setItems([]);
          setTotalCount(0);
          return;
        }
        if (res?.__error) {
          setUnavailable(false);
          if (!append) {
            setLoadError(true);
            setItems([]);
            setTotalCount(0);
          } else {
            toast.error(t("status_update.load_more_error"));
          }
          return;
        }
        setUnavailable(false);
        setLoadError(false);
        const rows = Array.isArray(res?.data) ? res.data : [];
        setItems((prev) => (append ? [...prev, ...rows] : rows));
        setTotalCount(resolveTotalCount(res, rows.length));
        setPage(pageNum);
      } catch {
        if (requestId !== requestIdRef.current) return;
        if (!append) {
          setLoadError(true);
          setItems([]);
          setTotalCount(0);
        } else {
          toast.error(t("status_update.load_more_error"));
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [t]
  );

  useEffect(() => {
    loadPage(1, { q: query });
  }, [query, loadPage]);

  const handleSearchChange = (value) => {
    setQueryInput(value);
    debouncedSetQuery(value);
  };

  const handleFiltersChange = (filters) => {
    const next = filters?.query ?? "";
    setQueryInput(next);
    debouncedSetQuery.cancel();
    setQuery(next);
    setPage(1);
  };

  const handleDeleted = (id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
    setTotalCount((n) => Math.max(0, n - 1));
  };

  const handleUpdated = (updated) => {
    if (!updated?.id) return;
    setItems((prev) =>
      prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
    );
  };

  const hasMore = items.length < totalCount;
  const listCount = totalCount > 0 ? String(totalCount) : null;

  if (unavailable) {
    return (
      <DashboardHeader
        variant="list"
        title={t("status_update.mine_title")}
        listCount={null}
        actions={[]}
      >
        <div className="space-y-2 p-3 md:space-y-4 md:p-5">
          <StatusUpdateUnavailableNotice
            onRetry={() => loadPage(1, { q: query })}
            retrying={loading}
          />
          <p className="text-sm text-slate-500">{t("status_update.unavailable_nav_hint")}</p>
        </div>
      </DashboardHeader>
    );
  }

  return (
    <DashboardHeader
      variant="list"
      title={t("status_update.mine_title")}
      listCount={listCount}
      actions={[]}
    >
      <div className="space-y-2 p-3 md:space-y-4 md:p-5">
        <div className="md:hidden">
          <div className="relative flex h-9 min-w-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-200">
            <MagnifyingGlassIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />
            <input
              type="text"
              value={queryInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t("status_update.mine_search_placeholder")}
              className="min-w-0 flex-1 border-none bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
            />
            {queryInput ? (
              <button
                type="button"
                onClick={() => handleSearchChange("")}
                className="flex-shrink-0 text-slate-400 hover:text-slate-600"
                aria-label={t("Clear")}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

        <div className="hidden md:block">
          <ModuleFilter
            filterCard
            hideStatusFilter
            acceptedStatuses={[]}
            disabled={loading}
            onFiltersChange={handleFiltersChange}
            initialFilters={{ query: queryInput }}
            searchPlaceholder={t("status_update.mine_search_placeholder")}
          />
        </div>

        {loading && items.length === 0 ? (
          <div className="py-12">
            <ResourceLoading />
          </div>
        ) : loadError && items.length === 0 ? (
          <div className="px-4 py-12 text-center" role="alert">
            <p className="text-sm font-semibold text-slate-800">
              {t("status_update.load_error")}
            </p>
            <button
              type="button"
              onClick={() => loadPage(1, { q: query })}
              className="mt-3 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50"
            >
              {t("status_update.retry")}
            </button>
          </div>
        ) : (
          <>
            <StatusUpdateFeed
              posts={items}
              assumeOwn
              emptyMessage={
                query
                  ? t("status_update.mine_empty_search")
                  : t("status_update.mine_empty")
              }
              onDeleted={handleDeleted}
              onUpdated={handleUpdated}
            />

            {hasMore && !loading ? (
              <div className="px-4 py-3 text-center">
                <button
                  type="button"
                  disabled={loadingMore}
                  onClick={() => loadPage(page + 1, { append: true, q: query })}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loadingMore
                    ? `${t("status_update.mine_load_more")}…`
                    : t("status_update.mine_load_more")}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </DashboardHeader>
  );
}

export default MyStatusUpdates;
